import styled from 'styled-components';

import { COLORS, ANIM, MOBILE } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';

/* ── Editor stage: a zoomable / pannable workspace holding the scaled canvas ───── */

export const Stage = styled.div`
  position: relative;
  width: 100%;
  height: min(70vh, 640px);
  min-height: 360px;
  border-radius: 16px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  touch-action: none;
  user-select: none;

  ${MOBILE} {
    height: min(58vh, 440px);
  }
`;

/* Pan + zoom layer. Positioned at the stage centre; the live transform (pan + scale) is inline. */
export const Transform = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  will-change: transform;
`;

/* A directly-manipulable element on the canvas: dragged to move, double-clicked to edit text.
   Position (left/top %) is inline; the selection ring is the accent, a hover ring hints it's live. */
export const Movable = styled.div<{ $selected: boolean; $editing?: boolean }>`
  position: absolute;
  /* Size to content, NOT to the space left of its position — otherwise an absolutely-positioned
     box near an edge shrink-fits to (canvas − left) and the text reflows to one char per line.
     Transform (translate-centre + rotation) is set inline so rotation can be dynamic. */
  width: max-content;
  max-width: 92cqw;
  border-radius: 4px;
  /* Promote the selected (draggable) element to its own layer so the compositor moves it with
     sub-pixel precision — otherwise the thin selection strokes jitter between 1↔2px while dragging. */
  will-change: ${({ $selected }) => ($selected ? 'transform' : 'auto')};
  cursor: ${({ $editing }) => ($editing ? 'text' : 'grab')};
  outline: 2px solid transparent;
  outline-offset: 5px;
  transition: outline-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    outline-color: ${({ $selected }) => ($selected ? 'transparent' : COLORS.hoverBorderLit)};
  }
  &:active {
    cursor: ${({ $editing }) => ($editing ? 'text' : 'grabbing')};
  }
`;

/* Zoom controls, pinned bottom-left of the stage. */
export const ZoomBar = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 10px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const ZoomBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 9px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${COLORS.surfaceMuted};
  }
`;

export const ZoomLabel = styled.span`
  min-width: 46px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  font-variant-numeric: tabular-nums;
`;

/* ── Selection handles (resize corners + a rotate handle) ─────────────────────────
   Rendered inside the selected Movable so they rotate/position with it. Sizes + the
   ring width are counter-scaled by the stage zoom (set inline) so they stay ~constant
   on screen. The frame passes clicks through; the handles re-enable pointer events. */
export const HandleFrame = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const Ring = styled.div`
  position: absolute;
  inset: 0;
  border-style: solid;
  border-color: ${COLORS.accent};
  border-radius: 4px;
`;

export const Handle = styled.div`
  position: absolute;
  background: ${COLORS.onAccent};
  border-style: solid;
  border-color: ${COLORS.accent};
  border-radius: 2px;
  pointer-events: auto;
  cursor: nwse-resize;
`;

export const RotateStem = styled.div`
  position: absolute;
  background: ${COLORS.accent};
  pointer-events: none;
`;

export const RotateHandle = styled.div`
  position: absolute;
  background: ${COLORS.onAccent};
  border-style: solid;
  border-color: ${COLORS.accent};
  border-radius: 50%;
  pointer-events: auto;
  cursor: grab;
`;

/* ── "+" add-element menu (bottom-right, aligned with the zoom bar) ───────────── */
export const AddBtn = styled.button`
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  cursor: pointer;
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  box-shadow: 0 8px 22px -8px ${COLORS.pageBg};
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    transform: translateY(-1px);
  }
`;

export const AddMenu = styled.div`
  position: absolute;
  right: 12px;
  bottom: 62px;
  width: 250px;
  max-height: 66%;
  overflow-y: auto;
  padding: 13px;
  border-radius: 14px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  box-shadow: 0 18px 44px -16px ${COLORS.pageBg};
`;

export const AddGroup = styled.div`
  & + & {
    margin-top: 14px;
  }
`;

export const AddGroupTitle = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  margin-bottom: 8px;
`;

export const AddGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

export const AddItem = styled.button`
  aspect-ratio: 1;
  border-radius: 10px;
  cursor: pointer;
  padding: 6px;
  display: grid;
  place-items: center;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

export const AddEmpty = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;
